import React, { useState, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import HierarchyNode from './HierarchyNode';
import { OccupationHierarchy } from './types';

interface HierarchyTreeProps {
  occupations: OccupationHierarchy[];
  onDragEnd: (event: DragEndEvent) => void;
  onUpdateHierarchy: (occupationId: number, parentId: number | null, display_priority: number) => Promise<void>;
  onUpdateOccupation: (occupationId: number, name: string, display_priority?: number) => Promise<void>;
  onDeleteOccupation: (occupationId: number) => Promise<void>;
  onTogglePublished: (occupationId: number) => Promise<void>;
}

interface TreeNode extends OccupationHierarchy {
  children: TreeNode[];
  expanded?: boolean;
}

const HierarchyTree: React.FC<HierarchyTreeProps> = ({
  occupations,
  onDragEnd,
  onUpdateHierarchy,
  onUpdateOccupation,
  onDeleteOccupation,
  onTogglePublished,
}) => {
  // デフォルトで全ノードを展開状態にする
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(() => {
    const allNodeIds = new Set<number>();
    occupations.forEach((occ) => {
      allNodeIds.add(occ.id);
    });
    return allNodeIds;
  });
  const [activeId, setActiveId] = useState<number | null>(null);

  // 新しい職種が追加された場合、展開状態に追加
  React.useEffect(() => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      occupations.forEach((occ) => {
        if (!next.has(occ.id)) {
          next.add(occ.id);
        }
      });
      return next;
    });
  }, [occupations.length]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // ツリー構造を構築
  const buildTree = (): TreeNode[] => {
    const occupationMap = new Map<number, TreeNode>();
    const roots: TreeNode[] = [];

    // まず全ての職種をマップに追加
    occupations.forEach((occ) => {
      occupationMap.set(occ.id, {
        ...occ,
        children: [],
        expanded: expandedNodes.has(occ.id),
      });
    });

    // 親子関係を構築
    occupations.forEach((occ) => {
      const node = occupationMap.get(occ.id)!;
      if (occ.parent_id === null) {
        roots.push(node);
      } else {
        const parent = occupationMap.get(occ.parent_id);
        if (parent) {
          parent.children.push(node);
        }
      }
    });

    // 各階層でソート（display_priority降順: 大きい値が優先度が高い）
    const sortByHierarchy = (nodes: TreeNode[]): TreeNode[] => {
      const sorted = [...nodes].sort((a, b) => {
        // display_priority降順でソート
        return b.display_priority - a.display_priority;
      });
      
      sorted.forEach((node) => {
        if (node.children.length > 0) {
          node.children = sortByHierarchy(node.children);
        }
      });
      return sorted;
    };

    return sortByHierarchy(roots);
  };

  // occupationsが変更されたらツリーを再構築
  // expandedNodesはツリー構造に影響しないので除外（展開/折りたたみのたびに再構築されるのを防ぐ）
  // occupationsの配列参照が変わるたびに再構築される（setOccupationsで新しい配列を作成しているため）
  const tree = useMemo(() => buildTree(), [occupations]);

  const toggleExpand = (nodeId: number) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  // すべてのノードを展開
  const expandAll = () => {
    const allNodeIds = new Set<number>();
    occupations.forEach((occ) => {
      allNodeIds.add(occ.id);
    });
    setExpandedNodes(allNodeIds);
  };

  // すべてのノードを閉じる
  const collapseAll = () => {
    setExpandedNodes(new Set<number>());
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as number);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null);
    await onDragEnd(event);
  };

  const activeOccupation = activeId
    ? occupations.find((occ) => occ.id === activeId)
    : null;

  // 第一階層へのドロップゾーン
  const FirstLevelDropZone: React.FC = () => {
    const { setNodeRef, isOver } = useDroppable({
      id: 'drop-first-level',
      data: {
        type: 'first-level',
      },
    });

    return (
      <div
        ref={setNodeRef}
        className={`mb-3 p-4 border-2 border-dashed rounded text-center transition-all ${
          isOver 
            ? 'border-primary bg-primary-subtle shadow-lg' 
            : 'border-secondary bg-light'
        }`}
        style={{ 
          minHeight: '80px', 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          transition: 'all 0.2s ease-in-out',
          transform: isOver ? 'scale(1.02)' : 'scale(1)',
        }}
      >
        {isOver ? (
          <>
            <i className="bi bi-arrow-up-circle-fill text-primary" style={{ fontSize: '2rem', marginBottom: '0.5rem' }}></i>
            <span className="text-primary fw-bold">
              ここにドロップして第一階層に移動
            </span>
            <small className="text-muted d-block mt-1">親職種を解除します</small>
          </>
        ) : (
          <>
            <i className="bi bi-arrow-up-circle text-muted" style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}></i>
            <span className="text-muted">
              第一階層に移動（ここにドロップ）
            </span>
          </>
        )}
      </div>
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="hierarchy-tree">
        <div className="mb-3 d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={expandAll}
            title="すべての階層を展開"
          >
            <i className="bi bi-chevron-down me-1"></i>
            すべて展開
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={collapseAll}
            title="すべての階層を閉じる"
          >
            <i className="bi bi-chevron-up me-1"></i>
            すべて閉じる
          </button>
        </div>
        <FirstLevelDropZone />
        {tree.map((node) => (
          <HierarchyNode
            key={node.id}
            node={node}
            level={1}
            expandedNodes={expandedNodes}
            onToggleExpand={toggleExpand}
            onUpdateHierarchy={onUpdateHierarchy}
            onUpdateOccupation={onUpdateOccupation}
            onDeleteOccupation={onDeleteOccupation}
            onTogglePublished={onTogglePublished}
            allOccupations={occupations}
          />
        ))}
      </div>
      <DragOverlay>
        {activeOccupation ? (
          <div className="p-3 border rounded bg-white shadow-lg">
            <span className="fw-semibold">{activeOccupation.name}</span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default HierarchyTree;

